/**
 * CloudServersBadge — the nav rail's fleet report. Nothing while the server list is loading or
 * empty, or while the portal banner shows, because the banner already names the fault. While
 * the notifications stream is down every status is unknown, so the rail says that rather than
 * calling the servers offline.
 */
import { useAtomValueRawSync } from 'jotai';
import type { ReactNode } from 'react';
import React from 'react';

import { usePortalUnavailable } from '@/hooks/usePortalUnavailable';
import { useServers } from '@/hooks/useServers';
import { liveStatusDownAtom } from '@/state/apiReads';

import { LiveStatusDownBadge } from './LiveStatusDownBadge';
import { ServersOfflineBadge } from './ServersOfflineBadge';

interface Props {
    isLabelled: boolean;
    isRoomy?: boolean;
}

export const CloudServersBadge = ({ isLabelled, isRoomy = false }: Props): ReactNode => {
    const { servers, isLoading } = useServers();
    const isLiveStatusDown = useAtomValueRawSync(liveStatusDownAtom);
    const { isUnavailable } = usePortalUnavailable();

    if (isLoading || servers.length === 0 || isUnavailable) {
        return null;
    }
    if (isLiveStatusDown) {
        return (
            <LiveStatusDownBadge
                isLabelled={isLabelled}
                isRoomy={isRoomy}
            />
        );
    }
    return (
        <ServersOfflineBadge
            isLabelled={isLabelled}
            isRoomy={isRoomy}
        />
    );
};
